import { useEffect } from 'react';

/**
 * Okunan gönderi yerinden kaymasın (mobil, 3 Kasım 2026 kullanıcı geri bildirimi). Görünür ilk kartın
 * üstündeki bir öğe (sonradan gelen modül, yüklenen fotoğraf, bugünün maçları) büyür ya da küçülürse
 * fark kadar kaydırılır; kart ekranda aynı yerde kalır. Tarayıcıların `overflow-anchor` davranışı,
 * WebKit'te (iOS Safari ve uygulama WebView'ı) yok, Chrome'da üstteki kart büyüyünce tutmuyor; kök öğede
 * `overflow-anchor: none` (iki kez kaydırılmasın) ve her tarayıcıda bu kanca.
 *
 * `watch`: boyutu izlenen öğeler; `anchor`: yerinde tutulacak kartlar (ikisi de `root` içinde).
 * Görünür kart yoksa (akış ekran dışında) dokunulmaz. Kartın kendi içindeki değişiklik telafi edilmez.
 */
export default function useScrollAnchor(
  root: React.RefObject<HTMLElement>,
  watch: string,
  anchor = '[data-feed-id]',
  // Kök öğe değişince (boş akış → liste) yeniden bağlanmak için.
  key: unknown = null,
) {
  useEffect(() => {
    const node = root.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const heights = new WeakMap<Element, number>();
    const visibleAnchor = () =>
      Array.from(node.querySelectorAll<HTMLElement>(anchor)).find(
        item =>
          item.getBoundingClientRect().top >= 0 &&
          item.getBoundingClientRect().top < window.innerHeight,
      );
    const observer = new ResizeObserver(entries => {
      // Sayfa en üstteyken (henüz kaydırılmadı) telafi yok: açılışta üst kısım doğal akışla dolar,
      // sayfa kendiliğinden aşağı kaymaz (tarayıcıların kendi sabitlemesi de böyle). Boyutlar yine kaydedilir.
      const atTop = window.scrollY <= 0;
      let shift = 0;
      const target = visibleAnchor();
      entries.forEach(entry => {
        const height = entry.target.getBoundingClientRect().height;
        const previous = heights.get(entry.target);
        heights.set(entry.target, height);
        if (previous === undefined || !target || entry.target === target) return;
        // Yalnız kartın üstünde kalan öğe (kart sırası belge sırasıyla aynı).
        const above =
          entry.target.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING &&
          !entry.target.contains(target);
        if (above) shift += height - previous;
      });
      if (!atTop && Math.abs(shift) >= 1) {
        const html = document.documentElement;
        const behavior = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollBy(0, shift);
        html.style.scrollBehavior = behavior;
      }
    });
    const observeAll = () =>
      node.querySelectorAll(watch).forEach(item => {
        if (!heights.has(item)) {
          heights.set(item, item.getBoundingClientRect().height);
          observer.observe(item);
        }
      });
    observeAll();
    // Yeni kart/modül eklendikçe izlemeye alınır (eklenmesi telafi edilmez; büyümesi edilir).
    const mutations = new MutationObserver(observeAll);
    mutations.observe(node, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [root, watch, anchor, key]);
}
